(() => {
  'use strict';
  function primaryFamily(category) {
    const name = category.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();
    const families = [/gode/, /gaine/, /anneau/, /vibromasseur|culotte vibrante/, /stimulateur/, /wand/, /œuf|oeuf/, /masturbateur/, /plug|chapelet anal/, /boules/, /pompe/];
    const rank = families.findIndex(pattern => pattern.test(name));
    return rank < 0 ? 100 : rank;
  }
  function optionsForStep(data, matrix, selected, step) {
    if (step === 0) return data.primary.filter(id => data.counts[id] > 0 && primaryFamily(data.items[id].category) < 100);
    const n = data.companions.length;
    if (!matrix || matrix.length !== n * n) return [];
    if (step === 1) return data.companions.filter((id, row) => {
      for (let col = 0; col < n; col++) if (matrix[row * n + col] > 0) return true;
      return false;
    });
    const row = data.companions.indexOf(selected[1]);
    if (row < 0) return [];
    return data.companions.filter((id, col) => matrix[row * n + col] > 0);
  }
  // Above 99 EUR the shop already ships for free: a kit is worth most to the
  // customer below it. Order: kits under 99 EUR first, then the biggest discount
  // in percent (a real deal without having to spend 100 EUR). Complements go
  // massage oil, lubricant, games, then the rest; the third avoids repeating the second.
  const FREE_SHIPPING_FROM = 9900;
  const COMPANION_ORDER = [/huile de massage/, /lubrifiant/, /jeu/, /bougie/, /gel de massage/, /creme/, /poudre/, /bain/, /nettoyant/];
  const plain = text => text.normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase();
  function companionRank(category) {
    const rank = COMPANION_ORDER.findIndex(pattern => pattern.test(plain(category)));
    return rank < 0 ? COMPANION_ORDER.length : rank;
  }
  function rankOptions(data, matrix, selected, step, ids) {
    const n = data.companions.length, price = id => data.items[id].price;
    const score = new Map();
    for (const id of ids) {
      let under = false, deal = -1, family;
      if (step === 0) {
        const best = data.bestDiscount?.[id];
        under = Number.isInteger(best) && best >= 0; deal = under ? best : -1;
        family = primaryFamily(data.items[id].category);
      } else {
        family = companionRank(data.items[id].category);
        if (step === 2 && data.items[id].category === data.items[selected[1]]?.category) family += COMPANION_ORDER.length + 1;
        const row = data.companions.indexOf(step === 1 ? id : selected[1]);
        const cols = step === 1 ? data.companions.map((_, col) => col) : [data.companions.indexOf(id)];
        for (const col of matrix && row >= 0 ? cols : []) {
          const kit = matrix[row * n + col];
          if (!kit) continue;
          const retail = price(selected[0]) + price(data.companions[row]) + price(data.companions[col]);
          const below = kit < FREE_SHIPPING_FROM, ppm = Math.floor((retail - kit) * 1e6 / retail);
          if (below && !under) { under = true; deal = -1; }
          if (below === under) deal = Math.max(deal, ppm);
        }
      }
      score.set(id, {under, deal, family});
    }
    return [...ids].sort((a, b) => {
      const x = score.get(a), y = score.get(b);
      if (x.under !== y.under) return x.under ? -1 : 1;
      if (step > 0 && x.family !== y.family) return x.family - y.family;
      return (y.deal - x.deal) || (price(a) - price(b)) || (x.family - y.family);
    });
  }
  function allocatePrices(prices, total) {
    const original = prices.reduce((sum, price) => sum + price, 0);
    const discount = original - total;
    const parts = prices.map(price => Math.floor(discount * price / original));
    let left = discount - parts.reduce((sum, part) => sum + part, 0);
    for (let i = 0; left > 0; i++, left--) parts[i % prices.length]++;
    return prices.map((price, i) => price - parts[i]);
  }
  function separateShipping(data, subtotal) {
    return subtotal >= (data.freeShippingThreshold ?? 9900) ? 0 : data.shipping;
  }
  if (typeof module !== 'undefined' && module.exports) module.exports = { optionsForStep, rankOptions, allocatePrices, separateShipping };
  if (typeof window === 'undefined' || customElements.get('letabli-kitcreator')) return;
  class KitCreator extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;
      this.labels = JSON.parse(this.querySelector('[data-labels]').textContent);
      this.step = 0; this.selected = [null, null, null]; this.limit = 24; this.matrix = null; this.sequence = 0;
      this.cache = new Map();
      this.grid = this.querySelector('[data-grid]'); this.status = this.querySelector('[data-status]');
      this.search = this.querySelector('[data-search]'); this.category = this.querySelector('[data-category]');
      this.summary = this.querySelector('[data-summary]');
      this.search.addEventListener('input', () => { this.limit = 24; this.renderGrid(); });
      this.category.addEventListener('change', () => { this.limit = 24; this.renderGrid(); });
      this.querySelector('[data-more]').addEventListener('click', () => { this.limit += 24; this.renderGrid(); });
      this.querySelector('[data-back]').addEventListener('click', () => this.changeStep(Math.max(0, this.step - 1)));
      this.start();
    }
    text(key) { return this.labels[key] || key; }
    money(cents) { return new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR' }).format(cents / 100); }
    element(tag, text, cls) { const e = document.createElement(tag); if (text !== undefined) e.textContent = text; if (cls) e.className = cls; return e; }
    button(text, action) { const b = this.element('button', text); b.type = 'button'; b.addEventListener('click', action); return b; }
    image(p) { const img = this.element('img'); img.src = p.image; img.alt = p.imageAlt || `${p.title} · ${p.variant}`; img.loading = 'lazy'; img.width = 280; img.height = 215; return img; }
    buildSheet() {
      const sheet = this.element('div', undefined, 'kc__sheet'); sheet.hidden = true;
      sheet.setAttribute('role', 'dialog'); sheet.setAttribute('aria-modal', 'true'); sheet.setAttribute('aria-labelledby', 'kc-sheet-title');
      const backdrop = this.element('div', undefined, 'kc__sheet-backdrop'); backdrop.addEventListener('click', () => this.closeDetails());
      const panel = this.element('div', undefined, 'kc__sheet-panel');
      const bar = this.element('div', undefined, 'kc__sheet-bar');
      const title = this.element('h3', '', 'kc__sheet-title'); title.id = 'kc-sheet-title'; title.setAttribute('data-sheet-title', '');
      const close = this.button(this.text('details_close'), () => this.closeDetails()); close.className = 'kc__sheet-close'; close.setAttribute('data-sheet-close', '');
      bar.append(title, close);
      const body = this.element('div', undefined, 'kc__sheet-body'); body.setAttribute('data-sheet-body', '');
      panel.append(bar, body); sheet.append(backdrop, panel); this.append(sheet);
      this.sheet = sheet; this.detailCache = new Map();
      this.onSheetKey = event => { if (event.key === 'Escape') this.closeDetails(); };
    }
    openDetails(id) {
      if (this.sheet && !this.sheet.hidden && this.sheetId === id) { this.closeDetails(); return; }
      if (!this.sheet) this.buildSheet();
      const p = this.data.items[id]; this.sheetId = id; this.sheetOpener = document.activeElement;
      this.sheet.querySelector('[data-sheet-title]').textContent = p.title;
      const body = this.sheet.querySelector('[data-sheet-body]');
      body.replaceChildren(this.element('p', this.text('details_loading'), 'kc__sheet-loading')); body.scrollTop = 0;
      this.sheet.hidden = false; requestAnimationFrame(() => this.sheet.classList.add('is-open'));
      document.documentElement.classList.add('kc-sheet-open'); document.addEventListener('keydown', this.onSheetKey);
      this.sheet.querySelector('[data-sheet-close]').focus({ preventScroll: true });
      const root = this.dataset.root.endsWith('/') ? this.dataset.root : this.dataset.root + '/';
      if (!this.detailCache.has(p.handle)) {
        this.detailCache.set(p.handle, this.fetchJSON(`${root}products/${encodeURIComponent(p.handle)}.js`)
          .catch(error => { this.detailCache.delete(p.handle); throw error; }));
      }
      this.detailCache.get(p.handle)
        .then(product => { if (this.sheetId === id) this.fillSheet(id, p, product); })
        .catch(() => { if (this.sheetId === id) body.replaceChildren(this.element('p', this.text('network_error'), 'kc__sheet-loading')); });
    }
    fillSheet(id, p, product) {
      const body = this.sheet.querySelector('[data-sheet-body]'); const parts = [];
      const images = (product.images || []).slice(0, 5);
      if (images.length) {
        const gallery = this.element('div', undefined, 'kc__sheet-gallery');
        for (const src of images) {
          const img = this.element('img'); img.src = src + (src.includes('?') ? '&' : '?') + 'width=600';
          img.alt = p.title; img.loading = 'lazy'; img.width = 300; img.height = 300; gallery.append(img);
        }
        parts.push(gallery);
      }
      const variant = ['Default Title', 'Taille unique'].includes(p.variant) ? '' : ` · ${p.variant}`;
      parts.push(this.element('p', `${this.money(p.price)}${variant}`, 'kc__sheet-price'));
      const description = this.element('div', undefined, 'kc__sheet-desc');
      description.innerHTML = product.description || '';
      description.querySelectorAll('script,style,iframe,form').forEach(node => node.remove());
      parts.push(description);
      const actions = this.element('div', undefined, 'kc__sheet-actions');
      actions.append(this.button(this.text('details_choose'), () => { this.closeDetails(); this.choose(id); }));
      if (product.url) {
        const link = this.element('a', this.text('details_page'));
        link.href = product.url; link.target = '_blank'; link.rel = 'noopener'; actions.append(link);
      }
      parts.push(actions);
      body.replaceChildren(...parts);
    }
    closeDetails() {
      if (!this.sheet || this.sheet.hidden) return;
      this.sheet.classList.remove('is-open'); this.sheetId = null;
      document.removeEventListener('keydown', this.onSheetKey);
      document.documentElement.classList.remove('kc-sheet-open');
      setTimeout(() => { if (!this.sheet.classList.contains('is-open')) this.sheet.hidden = true; }, 260);
      if (this.sheetOpener && this.sheetOpener.focus) this.sheetOpener.focus({ preventScroll: true });
    }
    async fetchJSON(url) {
      const response = await fetch(url, { signal: AbortSignal.timeout(20000) });
      if (!response.ok) throw new Error('Request failed');
      return response.json();
    }
    async cartRequest(url, options = {}) {
      const response = await fetch(url, {credentials:'same-origin', signal:AbortSignal.timeout(20000), ...options});
      if (!response.ok) throw new Error('Request failed');
      return response.json();
    }
    async start() {
      try {
        this.data = await this.fetchJSON(this.dataset.source);
        if (window.Shopify?.currency?.active && window.Shopify.currency.active !== this.data.currency) throw new Error('Currency');
        // The notice only shows while the kits cannot be ordered (preview or catalogue update).
        const notice = this.querySelector('.kc__preview');
        if (notice) notice.hidden = window.LetabliKitCart?.ready(this.data) === true;
        this.changeStep(0, false);
      } catch (error) { this.status.textContent = this.text(error.message === 'Currency' ? 'currency_error' : 'load_failed'); }
    }
    async loadMatrix(id) {
      const bucket = Math.floor(id / this.data.bucketSize);
      if (!this.cache.has(bucket)) {
        const url = new URL(this.dataset.source, window.location.href);
        url.pathname = url.pathname.replace(/[^/]+$/, this.data.quoteFiles[String(bucket)]);
        this.cache.set(bucket, await this.fetchJSON(url.toString()));
      }
      const encoded = this.cache.get(bucket)[String(id)];
      if (!encoded) throw new Error('Missing quote');
      const bytes = Uint8Array.from(atob(encoded), c => c.charCodeAt(0));
      const stream = new Blob([bytes]).stream().pipeThrough(new DecompressionStream('deflate'));
      const buffer = await new Response(stream).arrayBuffer();
      const expected = this.data.companions.length ** 2;
      if (buffer.byteLength !== expected * 4) throw new Error('Invalid quote');
      const view = new DataView(buffer);
      return Uint32Array.from({ length: expected }, (_, i) => view.getUint32(i * 4, true));
    }
    async choose(id) {
      if (this.step === 0) {
        const sequence = ++this.sequence;
        this.selected = [id, null, null]; this.matrix = null;
        this.status.textContent = this.text('working');
        this.querySelectorAll('[data-grid] button').forEach(b => b.disabled = true);
        try {
          const matrix = await this.loadMatrix(id);
          if (sequence !== this.sequence) return;
          this.matrix = matrix; this.changeStep(1);
        } catch {
          if (sequence !== this.sequence) return;
          this.renderGrid(); this.status.textContent = this.text('load_failed');
        }
      } else if (this.step === 1) {
        if (!optionsForStep(this.data, this.matrix, this.selected, 1).includes(id)) return;
        this.selected[1] = id; this.selected[2] = null; this.changeStep(2);
      } else {
        if (!optionsForStep(this.data, this.matrix, this.selected, 2).includes(id)) return;
        this.selected[2] = id; this.renderSelection(); this.renderSummary();
        this.querySelector('.kc__layout').scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }
    changeStep(step, scroll = true) {
      this.sequence++; this.step = step; this.limit = 24; this.search.value = ''; this.category.value = '';
      this.summary.hidden = true;
      this.classList.remove('is-complete');
      this.querySelector('[data-chosen-products]').hidden = true;
      this.querySelector('[data-stage-title]').textContent = this.text(['primary', 'secondary', 'third'][step]);
      this.querySelector('[data-back]').hidden = step === 0;
      this.querySelectorAll('[data-step]').forEach((e, i) => { if (i === step) e.setAttribute('aria-current', 'step'); else e.removeAttribute('aria-current'); });
      const opts = optionsForStep(this.data, this.matrix, this.selected, step);
      const categories = [...new Set(opts.map(i => this.data.items[i].category))].sort((a, b) => (step === 0 ? primaryFamily(a) - primaryFamily(b) : 0) || a.localeCompare(b, 'fr'));
      this.category.replaceChildren(new Option(this.text('all_categories'), ''), ...categories.map(c => new Option(c, c)));
      this.renderSelection(); this.renderGrid();
      if (scroll) this.querySelector('.kc__steps').scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
    renderSelection() {
      const container = this.querySelector('[data-selection]'); container.replaceChildren();
      let count = 0;
      this.selected.forEach((id, step) => {
        if (id === null || step >= this.step && this.selected[2] === null) {
          const placeholder = this.element('div', undefined, 'kc__slot');
          const text = this.element('div');
          text.append(this.element('strong', this.text(['slot_primary', 'slot_secondary', 'slot_third'][step])), this.element('small', this.text('pending')));
          placeholder.append(this.element('span', String(step + 1), 'kc__slot-number'), text); container.append(placeholder); return;
        }
        count++;
        const p = this.data.items[id]; const item = this.element('div', undefined, 'kc__selected');
        const variant = p.variant === 'Default Title' ? '' : ` · ${p.variant}`;
        const detail = this.element('div', undefined, 'kc__selected-detail');
        detail.append(this.element('span', `${p.title}${variant}`), this.element('strong', this.money(p.price)), this.button(this.text('change'), () => this.changeStep(step)));
        item.append(this.image(p), detail);
        container.append(item);
      });
      this.querySelector('[data-progress]').textContent = `${count} / 3`;
    }
    renderGrid() {
      if (!this.data) return;
      const normalize = s => s.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();
      const term = normalize(this.search.value.trim());
      const possible = optionsForStep(this.data, this.matrix, this.selected, this.step);
      const filtered = rankOptions(this.data, this.matrix, this.selected, this.step,
        possible.filter(id => { const p = this.data.items[id]; return (!this.category.value || p.category === this.category.value) && (!term || normalize(`${p.title} ${p.variant}`).includes(term)); }));
      this.grid.replaceChildren();
      const grouped = new Map();
      for (const id of filtered) { const key = this.data.items[id].productId; if (!grouped.has(key)) grouped.set(key, []); grouped.get(key).push(id); }
      const choices = [...grouped.values()];
      this.status.textContent = `${choices.length} ${this.text('allowed')}`;
      this.querySelector('[data-more]').hidden = choices.length <= this.limit;
      if (!filtered.length) { this.grid.append(this.element('p', this.text(possible.length ? 'no_results' : 'empty'), 'kc__message')); return; }
      for (const variants of choices.slice(0, this.limit)) {
        let id = variants[0];
        const p = this.data.items[id]; const card = this.element('article', undefined, 'kc__card');
        const variant = ['Default Title', 'Taille unique'].includes(p.variant) ? p.category : p.variant;
        const price = this.element('strong', this.money(p.price));
        const photo = this.image(p); const variantLabel = this.element('small', variant);
        card.append(photo, this.element('h3', p.title), variantLabel);
        // Fiche détaillée sans quitter la page (Nathan, 03/10/2026) : toucher pour ouvrir, retoucher pour fermer.
        const info = this.button(this.text('details'), () => this.openDetails(id)); info.className = 'kc__info';
        info.setAttribute('aria-haspopup', 'dialog');
        photo.classList.add('kc__photo-link'); photo.addEventListener('click', () => this.openDetails(id));
        card.append(info);
        if (variants.length > 1) {
          const select = this.element('select'); select.setAttribute('aria-label', p.title);
          select.replaceChildren(...variants.map(v => new Option(this.data.items[v].variant, String(v))));
          select.addEventListener('change', () => {
            id = Number(select.value); const chosen = this.data.items[id];
            price.textContent = this.money(chosen.price);
            variantLabel.textContent = ['Default Title', 'Taille unique'].includes(chosen.variant) ? chosen.category : chosen.variant;
            photo.src = chosen.image; photo.alt = chosen.imageAlt || `${chosen.title} · ${chosen.variant}`;
          });
          card.append(select);
        }
        card.append(price, this.button(this.text('select'), () => this.choose(id)));
        this.grid.append(card);
      }
    }
    selectedPrice() {
      const row = this.data.companions.indexOf(this.selected[1]); const col = this.data.companions.indexOf(this.selected[2]);
      return row >= 0 && col >= 0 && this.matrix ? this.matrix[row * this.data.companions.length + col] : 0;
    }
    renderSummary() {
      const total = this.selectedPrice(); if (!total) { this.summary.hidden = true; return; }
      const products = this.selected.map(id => this.data.items[id]); const sum = products.reduce((s, p) => s + p.price, 0);
      const individual = allocatePrices(products.map(p => p.price), total);
      const shipping = separateShipping(this.data, sum);
      this.classList.add('is-complete');
      this.querySelector('[data-stage-title]').textContent = this.text('summary');
      this.summary.replaceChildren(); this.summary.hidden = false;
      const cards = this.element('div', undefined, 'kc__summary-products');
      products.forEach((p, i) => {
        const card = this.element('article'); card.append(this.image(p), this.element('h4', p.title));
        if (p.variant !== 'Default Title') card.append(this.element('p', p.variant));
        card.append(this.element('p', `${this.text('normal_price')} : ${this.money(p.price)}`));
        const price = this.element('p'); price.append(this.element('span', `${this.text('kit_price')} : `), this.element('strong', this.money(individual[i]))); card.append(price, this.button(this.text('change'), () => this.changeStep(i))); cards.append(card);
      });
      const chosen = this.querySelector('[data-chosen-products]'); chosen.replaceChildren(cards); chosen.hidden = false;
      const dl = this.element('dl');
      const lines = [['products_total', sum], ['product_discount', -(sum - total)], ['discounted_total', total], ['delivery', 0], ['separate_total', sum + shipping], ['payable', total], ['saving', sum + shipping - total]];
      for (const [key, value] of lines) {
        const cls = key === 'payable' ? 'kc__total' : key === 'saving' ? 'kc__saving' : undefined;
        const dt = this.element('dt', this.text(key), cls); const dd = this.element('dd', undefined, cls);
        if (key === 'delivery') {
          if (shipping) dd.append(this.element('s', this.money(shipping)), document.createTextNode(' · '));
          dd.append(document.createTextNode(this.text('free')));
        }
        else dd.textContent = this.money(value);
        dl.append(dt, dd);
      }
      const saleReady = window.LetabliKitCart?.ready(this.data) === true;
      const note = this.element('p', `${this.text('saving_note')} ${this.text(saleReady ? 'one_kit' : 'no_order')}`, 'kc__note');
      const status = this.element('p', '', 'kc__review-status'); status.setAttribute('role', 'status');
      const button = this.button(this.text(saleReady ? 'add_kit' : 'review'), async () => {
        button.disabled = true; status.textContent = this.text(saleReady ? 'adding_kit' : 'checking');
        try {
          const root = this.dataset.root.endsWith('/') ? this.dataset.root : this.dataset.root + '/';
          if (saleReady) {
            if (!this.cartController) this.cartController = window.LetabliKitCart.controller({root,
              request:(url, options) => this.cartRequest(url, options),
              makeToken:() => 'kc1:' + crypto.randomUUID()});
            await this.cartController.add({data:this.data, products, expected:total,
              refresh:() => this.cartRequest(this.dataset.source, {cache:'no-store'})});
            window.location.assign(root + 'cart');
            return;
          }
          const results = await Promise.all(products.map(p => this.fetchJSON(`${root}products/${encodeURIComponent(p.handle)}.js`)));
          const correct = results.every((product, i) => { const variant = product.variants.find(v => String(v.id) === products[i].id); return variant?.available && variant.price === products[i].price; });
          status.textContent = this.text(correct ? 'verified' : 'changed');
        } catch (error) { status.textContent = this.text(this.labels[error.code] ? error.code : 'network_error'); }
        finally { button.disabled = false; }
      });
      button.className = 'kc__review'; this.summary.append(dl, note, button, status);
      if (saleReady) {
        const cartLink = this.element('a', this.text('cart_link'));
        cartLink.href = this.dataset.root.replace(/\/?$/, '/') + 'cart'; this.summary.append(cartLink);
      }
    }
  }
  customElements.define('letabli-kitcreator', KitCreator);
})();
